// AssignmentDialog — Zwei-Schritt-Modal für Funktions- und Springer-Zuweisung sowie Abwesenheits-Status
import React, { useState, useEffect } from 'react';
import { Truck, User, AlertCircle, CheckCircle2, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatDate } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import {
  extractFahrzeugNummer,
  extractFahrzeugTyp,
  countFahrzeugeByTyp,
  getFahrzeugFarbe,
  getKontrastFarbe,
  STATUS_CODES,
} from './constants';
import SpringerEditSection from './SpringerEditSection';
import {
  buildExistingAushilfeUpdate,
  getSpringerForAssignment,
  hasAushilfeUserChanged,
} from './assignmentPayload';

export function AssignmentDialog({
  open,
  onOpenChange,
  mitarbeiter,
  diensttag,
  existingData,
  fahrzeuge,
  fahrzeugtypen,
  besetztePositionen,
  fahrzeugTypNummern,
  onAssign,
  alleMitarbeiter,  // Optional: Liste aller Wache-Mitarbeiter für Aushilfe-Auswahl (nur bei Gast-Slot relevant)
  aktuelleAbteilungId,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState('funktion');
  const [showSpringerModal, setShowSpringerModal] = useState(false);
  const [selectedHauptfunktion, setSelectedHauptfunktion] = useState(null);
  const [selectedSpringer, setSelectedSpringer] = useState([]);
  const [aushilfeUserId, setAushilfeUserId] = useState('');

  // Praktikanten-Modus: andere Validierungen, keine Springer, andere Texte
  const istPraktikant = !!mitarbeiter?.ist_praktikant;
  const istGastSlot = !!mitarbeiter?.ist_gast_slot;

  useEffect(() => {
    if (open) {
      setMode('funktion');
      setShowSpringerModal(false);
      setSelectedHauptfunktion(null);
      setSelectedSpringer([]);
      setAushilfeUserId(existingData?.aushilfe_user_id || '');
    }
  }, [open]);

  const isPositionBesetzt = (fahrzeugId, funktionKuerzel) => {
    if (funktionKuerzel === 'ZUS') return false;
    // Auch Praktikanten mit Funktion blockieren die Position wie normale Mitarbeiter.
    // Aber: der aktuelle Mitarbeiter selbst darf sich nicht selbst blockieren —
    // sonst kann er nicht seine eigene Funktion ändern/beibehalten.
    if (!besetztePositionen) return false;
    return besetztePositionen.some(
      bp => bp.funktion_kuerzel === funktionKuerzel
         && bp.fahrzeug_id === fahrzeugId
         && bp.mitarbeiter_id !== mitarbeiter?.id
    );
  };

  const getBesetztVon = (fahrzeugId, funktionKuerzel) => {
    if (!besetztePositionen) return null;
    return besetztePositionen.find(
      bp => bp.funktion_kuerzel === funktionKuerzel
         && bp.fahrzeug_id === fahrzeugId
         && bp.mitarbeiter_id !== mitarbeiter?.id
    )?.mitarbeiter_name || null;
  };

  const handleSelectHauptfunktion = (fahrzeug, funktion) => {
    if (isPositionBesetzt(fahrzeug.id, funktion.kuerzel)) {
      toast.error(`${funktion.kuerzel} auf ${fahrzeug.funkrufname} ist bereits besetzt!`);
      return;
    }

    const fahrzeugNummer = fahrzeugTypNummern?.[fahrzeug.id]
      ?? extractFahrzeugNummer(fahrzeug.funkrufname);
    const isZus = funktion.kuerzel === 'ZUS';
    // Wenn das Kürzel selbst bereits mit einer Ziffer endet (z. B. 'RTW1B'),
    // enthält es die Fahrzeug-Nummer schon → NICHT nochmal anhängen.
    const kuerzelEndetMitZiffer = /\d$/.test(funktion.kuerzel || '');
    const haupt = {
      fahrzeug,
      funktion,
      displayName: isZus
        ? `Mitfahrer ${fahrzeug.funkrufname.split(' ')[0] || fahrzeug.funkrufname}`
        : (fahrzeugNummer && !kuerzelEndetMitZiffer
            ? `${funktion.kuerzel}${fahrzeugNummer}`
            : funktion.kuerzel),
    };
    setSelectedHauptfunktion(haupt);

    // Hauptfunktion direkt zuweisen — KEIN Auto-Springer-Modal mehr.
    // Springer können nachträglich über die "Springer anhängen"-Section
    // im Click-to-Edit-Modus hinzugefügt werden.
    directAssign(haupt);
  };

  const directAssign = async (haupt, springerOverride = null) => {
    setIsLoading(true);
    try {
      // Bei Reassignment ohne explizite Springer-Liste: bestehende Springer übernehmen
      const springerFuerSave = getSpringerForAssignment(
        haupt.funktion.kuerzel,
        springerOverride,
        existingData?.sprungfunktionen || []
      );

      await onAssign({
        type: 'funktion_mit_springer',
        mitarbeiter_id: mitarbeiter.id,
        datum: diensttag.datum,
        dienst_art: diensttag.dienst_art,
        fahrzeug_id: haupt.fahrzeug.id,
        funktion_kuerzel: haupt.funktion.kuerzel,
        springer_funktionen: springerFuerSave,
        ist_praktikant: istPraktikant,
        aushilfe_user_id: istGastSlot && aushilfeUserId ? aushilfeUserId : null,
      });
      const springerText = springerFuerSave.length > 0
        ? ` + ${springerFuerSave.length} Springer`
        : '';
      toast.success(`${istPraktikant ? '[P] ' : ''}${haupt.displayName}${springerText} zugewiesen`);
      onOpenChange(false);
    } catch (error) {
      console.error('Direct assign error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSpringer = (fahrzeug, funktion) => {
    const key = `${fahrzeug.id}_${funktion.kuerzel}`;
    const exists = selectedSpringer.find(s => s.key === key);

    if (exists) {
      setSelectedSpringer(prev => prev.filter(s => s.key !== key));
    } else {
      const fahrzeugKurzName = funktion.fahrzeugKurzName || fahrzeug.funkrufname.split(' ')[0] || 'FZ';
      setSelectedSpringer(prev => [...prev, {
        key,
        fahrzeug,
        funktion,
        displayName: fahrzeugKurzName,
        fullDescription: `${funktion.kuerzel} ${fahrzeugKurzName}`,
      }]);
    }
  };

  const handleConfirmAssignment = async () => {
    if (!selectedHauptfunktion) return;

    setIsLoading(true);
    try {
      await onAssign({
        type: 'funktion_mit_springer',
        mitarbeiter_id: mitarbeiter.id,
        datum: diensttag.datum,
        dienst_art: diensttag.dienst_art,
        fahrzeug_id: selectedHauptfunktion.fahrzeug.id,
        funktion_kuerzel: selectedHauptfunktion.funktion.kuerzel,
        springer_funktionen: selectedSpringer.map(s => ({
          fahrzeug_id: s.fahrzeug.id,
          funktion_kuerzel: s.funktion.kuerzel,
        })),
        ist_praktikant: istPraktikant,
        aushilfe_user_id: istGastSlot && aushilfeUserId ? aushilfeUserId : null,
      });

      const prefix = istPraktikant ? '[P] ' : '';
      const springerText = selectedSpringer.length > 0
        ? ` + ${selectedSpringer.map(s => s.fullDescription || s.displayName).join(', ')}`
        : '';
      toast.success(`${prefix}${selectedHauptfunktion.displayName}${springerText} zugewiesen`);
      setShowSpringerModal(false);
      onOpenChange(false);
    } catch (error) {
      console.error('Assignment error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipSpringer = async () => {
    setSelectedSpringer([]);
    await handleConfirmAssignment();
  };

  const handleAssignStatus = async (code) => {
    setIsLoading(true);
    try {
      await onAssign({
        type: 'status',
        mitarbeiter_id: mitarbeiter.id,
        datum: diensttag.datum,
        dienst_art: diensttag.dienst_art,
        status_code: code,
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Status error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = async () => {
    setIsLoading(true);
    try {
      await onAssign({
        type: 'clear',
        mitarbeiter_id: mitarbeiter.id,
        datum: diensttag.datum,
        dienst_art: diensttag.dienst_art,
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Clear error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAushilfeName = async () => {
    if (!existingData?.hauptfunktion) return;

    setIsLoading(true);
    try {
      await onAssign(buildExistingAushilfeUpdate({
        existingData,
        mitarbeiterId: mitarbeiter.id,
        datum: diensttag.datum,
        dienstArt: diensttag.dienst_art,
        aushilfeUserId,
        istPraktikant,
      }));
      toast.success(aushilfeUserId ? 'Gastname aktualisiert' : 'Gast wieder anonymisiert');
      onOpenChange(false);
    } catch (error) {
      console.error('Aushilfe save error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!mitarbeiter || !diensttag) return null;

  const alleFahrzeuge = (fahrzeuge || [])
    .filter(fz => fz.status !== 'AUSSERDIENST')
    .sort((a, b) => (a.sortierung || 0) - (b.sortierung || 0));

  const getFunktionenFuerFahrzeug = (fz) => {
    // Einheitliche Fahrzeug-Farbe für ALLE Funktions-Buttons eines Fahrzeugs
    const fzFarbe = getFahrzeugFarbe(fz);

    if (fz.funktionen && fz.funktionen.length > 0) {
      return fz.funktionen.map((fn) => ({
        kuerzel: fn.kuerzel,
        name: fn.name,
        farbe: fzFarbe,
      }));
    }

    if (fahrzeugtypen && fahrzeugtypen.length > 0) {
      let typ = null;
      if (fz.fahrzeugtyp_id) {
        typ = fahrzeugtypen.find(t => t.id === fz.fahrzeugtyp_id);
      }
      if (!typ && fz.fahrzeugtyp_code) {
        typ = fahrzeugtypen.find(t => t.typ_code === fz.fahrzeugtyp_code);
      }
      if (!typ) {
        const typCodeFromName = extractFahrzeugTyp(fz.funkrufname);
        typ = fahrzeugtypen.find(t => t.typ_code === typCodeFromName);
      }

      if (typ && typ.standard_funktionen && typ.standard_funktionen.length > 0) {
        return typ.standard_funktionen.map((fn) => ({
          kuerzel: fn.kuerzel,
          name: fn.name,
          farbe: fzFarbe,
        }));
      }
    }

    return [
      { kuerzel: 'FF', name: 'Fahrzeugführer', farbe: fzFarbe },
      { kuerzel: 'Ma', name: 'Maschinist', farbe: fzFarbe },
    ];
  };

  const hauptFahrzeuge = alleFahrzeuge.filter(fz =>
    !fz.ist_springer && fz.dienstzeit_modell !== 'SPRINGER'
  );

  const springerFahrzeuge = alleFahrzeuge.filter(fz =>
    fz.kann_besprungen_werden || fz.ist_springer || fz.dienstzeit_modell === 'SPRINGER'
  );
  const aushilfeNameGeaendert = istGastSlot
    && !!existingData?.hauptfunktion
    && hasAushilfeUserChanged(existingData, aushilfeUserId);

  // Schritt 2: Springer-Modal
  if (showSpringerModal && selectedHauptfunktion) {
    return (
      <Dialog open={open} onOpenChange={(isOpen) => {
        if (!isOpen) setShowSpringerModal(false);
        onOpenChange(isOpen);
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Springer-Funktionen
            </DialogTitle>
            <DialogDescription>
              Hauptfunktion: <Badge
                className="text-white ml-1"
                style={{ backgroundColor: getFahrzeugFarbe(selectedHauptfunktion.fahrzeug) }}
              >
                {selectedHauptfunktion.displayName}
              </Badge>
              <span className="ml-2 text-muted-foreground">
                auf {selectedHauptfunktion.fahrzeug.funkrufname}
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Wählen Sie optional zusätzliche Springer-Funktionen:
            </p>

            {springerFahrzeuge.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Keine Springer-Fahrzeuge verfügbar</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                {springerFahrzeuge.map((fz) => {
                  const fahrzeugKurzName = fz.funkrufname.split(' ')[0] || fz.fahrzeugtyp_code || 'FZ';
                  const fahrzeugFunktionen = getFunktionenFuerFahrzeug(fz).map(fn => ({
                    kuerzel: fn.kuerzel, name: fn.name,
                  }));

                  return (
                    <div key={fz.id} className="border rounded-lg p-3 bg-amber-50/50 border-amber-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Truck className="w-4 h-4 text-amber-600" />
                        <span className="font-semibold text-sm">{fz.funkrufname}</span>
                        <Badge variant="outline" className="text-xs">{fz.fahrzeugtyp_code || ''}</Badge>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {fahrzeugFunktionen.map((fn) => {
                          const key = `${fz.id}_${fn.kuerzel}`;
                          const isSelected = selectedSpringer.some(s => s.key === key);

                          return (
                            <Button
                              key={fn.kuerzel}
                              variant={isSelected ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => toggleSpringer(fz, { ...fn, fahrzeugKurzName })}
                              className={cn(
                                'h-9',
                                isSelected && 'bg-amber-600 hover:bg-amber-700'
                              )}
                              title={`${fn.name} auf ${fz.funkrufname}`}
                            >
                              {isSelected && <CheckCircle2 className="w-3 h-3 mr-1" />}
                              {fn.kuerzel} {fahrzeugKurzName}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {selectedSpringer.length > 0 && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm font-medium text-emerald-800 mb-2">
                  Ausgewählte Springer-Funktionen:
                </p>
                <div className="flex flex-wrap gap-1">
                  {selectedSpringer.map((s) => (
                    <Badge
                      key={s.key}
                      variant="secondary"
                      className="bg-emerald-100 text-emerald-800"
                      title={s.fullDescription}
                    >
                      +{s.fullDescription || s.displayName}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-2 border-t">
            <Button
              variant="outline"
              onClick={handleSkipSpringer}
              disabled={isLoading}
              className="flex-1"
            >
              Keine Springer
            </Button>
            <Button
              onClick={handleConfirmAssignment}
              disabled={isLoading}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            >
              {isLoading
                ? <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Bestätigen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Schritt 1: Funktions- / Status-Auswahl
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            {istPraktikant && (
              <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-400">
                Praktikant
              </Badge>
            )}
            {istGastSlot && (
              <Badge variant="outline" className="bg-violet-100 text-violet-800 border-violet-400">
                Gast
              </Badge>
            )}
            Einteilung: {mitarbeiter.nachname}
          </DialogTitle>
          <DialogDescription>
            {formatDate(diensttag.datum, { weekday: 'short', day: '2-digit', month: 'short' })} • {' '}
            {diensttag.dienst_art === 'T' ? '☀️ Tag' : '🌙 Nacht'}
            {istPraktikant && (
              <span className="ml-2 text-amber-700 text-xs italic">
                Zählt nicht zur Mindeststärke
              </span>
            )}
            {istGastSlot && (
              <span className="ml-2 text-violet-700 text-xs italic">
                Externer Gast - in Statistik nur als &quot;Gast&quot;
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={mode} onValueChange={setMode} className="w-full flex-1 flex flex-col overflow-hidden">
          <TabsList className={cn(
            'w-full mb-4 shrink-0',
            (istPraktikant || istGastSlot) ? 'grid grid-cols-1' : 'grid grid-cols-2'
          )}>
            <TabsTrigger value="funktion" className="text-sm">
              <Truck className="w-4 h-4 mr-1" />
              Funktion
            </TabsTrigger>
            {!istPraktikant && !istGastSlot && (
              <TabsTrigger value="status" className="text-sm">
                <AlertCircle className="w-4 h-4 mr-1" />
                Abwesend
              </TabsTrigger>
            )}
          </TabsList>

          {istGastSlot && (
            <div className="mb-4 p-3 bg-violet-50 border border-violet-200 rounded-md space-y-2">
              <label className="text-xs font-semibold text-violet-900 uppercase tracking-wide">
                Aushilfe aus anderer Wachabteilung (optional)
              </label>
              <select
                value={aushilfeUserId}
                onChange={(e) => setAushilfeUserId(e.target.value)}
                data-testid="aushilfe-user-select"
                className="w-full px-2 py-1.5 text-sm border border-violet-300 bg-white rounded"
              >
                <option value="">— Anonymer Gast —</option>
                {(() => {
                  // Erst nach Wachabteilung gruppieren, innerhalb dann nach Nachname sortieren.
                  // Native <optgroup> gibt dem User die klare Trennung ohne extra CSS.
                  const kandidaten = (alleMitarbeiter || []).filter(
                    (m) => m?.abteilung_id && m.abteilung_id !== aktuelleAbteilungId
                  );
                  const byAbteilung = new Map();
                  for (const m of kandidaten) {
                    const label = m.abteilung_name || 'Unbekannte Wachabteilung';
                    if (!byAbteilung.has(label)) byAbteilung.set(label, []);
                    byAbteilung.get(label).push(m);
                  }
                  const gruppen = Array.from(byAbteilung.entries()).sort(([a], [b]) =>
                    a.localeCompare(b, 'de')
                  );
                  return gruppen.map(([abtName, users]) => {
                    users.sort((a, b) => (a.nachname || '').localeCompare(b.nachname || '', 'de'));
                    return (
                      <optgroup key={abtName} label={abtName}>
                        {users.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.nachname}, {m.vorname}
                          </option>
                        ))}
                      </optgroup>
                    );
                  });
                })()}
              </select>
              {aushilfeNameGeaendert && (
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSaveAushilfeName}
                  disabled={isLoading}
                  className="w-full bg-violet-700 hover:bg-violet-800"
                  data-testid="btn-save-aushilfe-name"
                >
                  {isLoading
                    ? <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Gastnamen speichern
                </Button>
              )}
              <p className="text-[10px] text-violet-700">
                Wenn gewählt: Der Dienst erscheint in „Nächster Dienst&quot; des Mitarbeiters. Statistik bleibt bei „Gast&quot;.
              </p>
            </div>
          )}

          <TabsContent value="funktion" className="flex-1 overflow-y-auto space-y-3 pr-2">
            {existingData?.hauptfunktion && (
              <SpringerEditSection
                existingData={existingData}
                springerFahrzeuge={springerFahrzeuge}
                mitarbeiter={mitarbeiter}
                diensttag={diensttag}
                fahrzeugTypNummern={fahrzeugTypNummern}
                istPraktikant={istPraktikant}
                onSave={onAssign}
                onClose={() => onOpenChange(false)}
              />
            )}

            {hauptFahrzeuge.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <Truck className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>Keine Fahrzeuge verfügbar</p>
              </div>
            ) : (
              hauptFahrzeuge.map((fz) => {
                const typCode = fz.fahrzeugtyp_code || extractFahrzeugTyp(fz.funkrufname);
                const funktionen = getFunktionenFuerFahrzeug(fz);
                const fahrzeugNummer = fahrzeugTypNummern?.[fz.id]
                  ?? extractFahrzeugNummer(fz.funkrufname);
                const kannBesprungen = fz.kann_besprungen_werden;
                const sollNummerZeigen = !!fahrzeugNummer;
                // Fahrzeug-Freigabe: Praktikanten haben keine Sperren.
                const gesperrteFn = istPraktikant ? [] : (mitarbeiter?.gesperrte_funktionen || []);
                const getSperre = (kuerzel) =>
                  gesperrteFn.find(g => g.fahrzeug_id === fz.id && g.funktion_kuerzel === kuerzel);

                return (
                  <div key={fz.id} className="border rounded-lg p-2 bg-muted/30">
                    <div className="flex items-center gap-2 mb-2 pb-1 border-b">
                      <Truck className="w-4 h-4 text-slate-600" />
                      <span className="font-bold text-sm truncate min-w-0">{fz.funkrufname}</span>
                      <Button
                        onClick={() => handleSelectHauptfunktion(
                          fz,
                          { kuerzel: 'ZUS', name: 'Zusätzliche Person' }
                        )}
                        disabled={isLoading}
                        size="sm"
                        variant="ghost"
                        className="h-6 px-1.5 text-[10px] font-semibold text-amber-800 hover:bg-amber-100 hover:text-amber-900"
                        title="Ohne Sitzfunktion mitfahren (zählt nicht zur Mindeststärke)"
                        data-testid={`btn-zus-${fz.id}`}
                      >
                        + Mitfahrer
                      </Button>
                      <span className="flex-1" />
                      <Badge variant="secondary" className="text-xs">{typCode}</Badge>
                      {kannBesprungen && (
                        <Badge variant="outline" className="text-xs text-amber-600 border-amber-400">Springer</Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-1.5">
                      {funktionen.map((fn) => {
                        const besetzt = isPositionBesetzt(fz.id, fn.kuerzel);
                        const besetztVon = getBesetztVon(fz.id, fn.kuerzel);
                        const sperre = getSperre(fn.kuerzel);
                        const kuerzelHatZiffer = /\d$/.test(fn.kuerzel || '');
                        const displayKuerzel = (sollNummerZeigen && !kuerzelHatZiffer)
                          ? `${fn.kuerzel}${fahrzeugNummer}`
                          : fn.kuerzel;
                        const isDisabled = isLoading || besetzt || !!sperre;
                        const tooltipText = sperre
                          ? (sperre.grund ? `Keine Freigabe — ${sperre.grund}` : 'Keine Freigabe für diese Funktion')
                          : (besetzt && besetztVon ? besetztVon : null);

                        return (
                          <Tooltip key={fn.kuerzel}>
                            <TooltipTrigger asChild>
                              <Button
                                onClick={() => !sperre && handleSelectHauptfunktion(fz, fn)}
                                disabled={isDisabled}
                                size="sm"
                                className={cn(
                                  'h-10 flex-col gap-0 text-white text-xs transition',
                                  besetzt && !sperre && 'bg-gray-400 opacity-60',
                                  sperre && 'bg-red-100 !text-red-700 opacity-70 line-through',
                                  !besetzt && !sperre && 'hover:brightness-110'
                                )}
                                style={!besetzt && !sperre
                                  ? { backgroundColor: fn.farbe, color: getKontrastFarbe(fn.farbe) }
                                  : undefined}
                                data-testid={`funktion-btn-${fz.id}-${fn.kuerzel}`}
                              >
                                <span className="font-bold">{displayKuerzel}</span>
                                <span className="text-[9px] opacity-80">
                                  {sperre ? '⊘' : (besetzt ? '✓' : '')}
                                </span>
                              </Button>
                            </TooltipTrigger>
                            {tooltipText && (
                              <TooltipContent><p>{tooltipText}</p></TooltipContent>
                            )}
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}

            {springerFahrzeuge.length > 0 && (
              <div className="flex items-center gap-2 pt-2 px-2 py-2 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                <span className="text-xs text-amber-800">
                  Nach Auswahl der Hauptfunktion können Sie optional Springer-Funktionen hinzufügen.
                </span>
              </div>
            )}
          </TabsContent>

          <TabsContent value="status" className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(STATUS_CODES).map(([code, info]) => (
                <Button
                  key={code}
                  variant="outline"
                  data-testid={`btn-status-${code}`}
                  className={cn(
                    'h-20 flex-col gap-1 transition-transform hover:scale-105',
                    info.bgLight,
                    info.border,
                    'border-2'
                  )}
                  onClick={() => handleAssignStatus(code)}
                  disabled={isLoading}
                >
                  <span className={cn('text-2xl font-bold', info.textColor)}>{info.displayCode || code}</span>
                  <span className="text-sm text-muted-foreground">{info.label}</span>
                </Button>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {existingData && (
          <div className="border-t pt-4 mt-2 shrink-0">
            <Button
              variant="destructive"
              size="sm"
              onClick={handleClear}
              disabled={isLoading}
              className="w-full"
            >
              <X className="w-4 h-4 mr-2" />
              Einteilung löschen
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AssignmentDialog;
